import './styles.scss';

import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';

import { memo, useCallback, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import { BacktesterStrategyData } from '../../../../../providers/backtesting/api/backtester-config-api.types';

import { ATMVolsChart } from './atm-vols-chart';
import { PerpSpreadChart } from './perp-spread-chart';
import { SpotPriceChart } from './spot-price-chart';

const ResponsiveGridLayout = WidthProvider(Responsive);

const INITIAL_LAYOUTS = {
  lg: [
    { i: 'market-data-spot-price-chart', x: 0, y: 0, w: 12, h: 4, minW: 4, minH: 3 },
    { i: 'market-data-atm-vols-chart', x: 0, y: 4, w: 12, h: 4, minW: 4, minH: 3 },
    { i: 'market-data-perp-spread-chart', x: 0, y: 8, w: 12, h: 4, minW: 4, minH: 3 },
  ],
  md: [
    { i: 'market-data-spot-price-chart', x: 0, y: 0, w: 12, h: 4, minW: 4, minH: 3 },
    { i: 'market-data-atm-vols-chart', x: 0, y: 4, w: 12, h: 4, minW: 4, minH: 3 },
    { i: 'market-data-perp-spread-chart', x: 0, y: 8, w: 12, h: 4, minW: 4, minH: 3 },
  ],
  sm: [
    { i: 'market-data-spot-price-chart', x: 0, y: 0, w: 12, h: 4, minW: 6, minH: 3 },
    { i: 'market-data-atm-vols-chart', x: 0, y: 4, w: 12, h: 4, minW: 6, minH: 3 },
    { i: 'market-data-perp-spread-chart', x: 0, y: 8, w: 12, h: 4, minW: 6, minH: 3 },
  ],
  xs: [
    { i: 'market-data-spot-price-chart', x: 0, y: 0, w: 1, h: 4 },
    { i: 'market-data-atm-vols-chart', x: 0, y: 4, w: 1, h: 4 },
    { i: 'market-data-perp-spread-chart', x: 0, y: 8, w: 1, h: 4 },
  ],
  xxs: [
    { i: 'market-data-spot-price-chart', x: 0, y: 0, w: 1, h: 4 },
    { i: 'market-data-atm-vols-chart', x: 0, y: 4, w: 1, h: 4 },
    { i: 'market-data-perp-spread-chart', x: 0, y: 8, w: 1, h: 4 },
  ],
};

const areLayoutsEqual = (left: any, right: any) => JSON.stringify(left) === JSON.stringify(right);

interface MarketDataProps {
  strategyData: BacktesterStrategyData;
  selectedStrategy: string;
}

export const MarketData = memo(({ strategyData, selectedStrategy }: MarketDataProps) => {
  const [layouts, setLayouts] = useState<any>(INITIAL_LAYOUTS);
  const scenario = strategyData[selectedStrategy];
  const reports = scenario?.pnl_reports ?? [];
  const token = scenario?.stratlet.account.token?.trim() || 'BTC';
  const handleLayoutChange = useCallback((_: unknown, updatedLayouts: any) => {
    setLayouts((prevLayouts: any) =>
      areLayoutsEqual(prevLayouts, updatedLayouts) ? prevLayouts : updatedLayouts,
    );
  }, []);

  return (
    <div className="backtest-result-view backtest-result-market-data">
      <ResponsiveGridLayout
        className="market-data-grid"
        layouts={layouts}
        autoSize={true}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 }}
        resizeHandles={['se']}
        draggableHandle=".drag-area"
        onLayoutChange={handleLayoutChange}
        useCSSTransforms={true}
        margin={[8, 8]}
      >
        <div key="market-data-spot-price-chart">
          <SpotPriceChart reports={reports} token={token} />
        </div>
        <div key="market-data-atm-vols-chart">
          <ATMVolsChart reports={reports} />
        </div>
        <div key="market-data-perp-spread-chart">
          <PerpSpreadChart reports={reports} />
        </div>
      </ResponsiveGridLayout>
    </div>
  );
});

MarketData.displayName = 'MarketData';
